import React, { createContext, useContext, ReactNode } from "react";
import { useAuth } from "./supabase-auth";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { UserProfile } from "./supabase-auth";
// import { useCheckout } from "BEDROCK/pages/checkout/checkout-provider";
type Role = "admin" | "user";

type AuthContextType = {
  client: SupabaseClient | undefined;
  session: Session | null;
  isLoading: boolean;
  userID: string | null;
  email: string | null;
  userProfile: any | null;
  fetchUserProfile: () => Promise<UserProfile>;
  role: Role | null;
  changePassword: (newPassword: string) => void;
  signOut: () => Promise<void>; // Call signOut on useSignOutHandler instead - it'll clear up local storage and clear checkout
  resetPassword: () => Promise<void>;
  requestAccountDeletion: () => Promise<void>;
  changeEmailAddress: (newEmail: string) => Promise<void>;
  isUserAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = (props: AuthProviderProps) => {
  const {
    session,
    isLoading,
    role,
    userID,
    email,
    signOut: authSignOut,
    isUserAuthenticated,
    resetPassword,
    requestAccountDeletion,
    changeEmailAddress,
    userProfile,
    fetchUserProfile,
    changePassword,
    client,
  } = useAuth();

  const signOut = async (): Promise<void> => {
    await authSignOut();
  };

  return (
    <AuthContext.Provider
      value={{
        client,
        session,
        isLoading,
        role,
        signOut, // Call signOut on useSignOutHandler instead - it'll clear up local storage and clear checkout
        isUserAuthenticated,
        userID,
        email,
        userProfile,
        fetchUserProfile,
        changePassword,
        resetPassword,
        requestAccountDeletion,
        changeEmailAddress,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
